/**
 * One renderer for every money surface: the cart, the order summary, the
 * receipt email, the Slack alert and the dashboard.
 *
 * Each of those previously formatted money its own way, and the results
 * disagreed. The receipt back-solved its subtotal with
 * `Math.round(total / 1.08875)` and its tax with `Math.round(total - subtotal)`,
 * so the line items provably did not add up to the total printed beneath them.
 * The cart interpolated raw floats and rendered `$167.4` and
 * `$117.41999999999999`. Slack called `.toFixed(2)` on a value that arrives
 * from Postgres as a string, threw, and was swallowed by a `try/catch` — so the
 * alert simply never appeared.
 *
 * Rendering from a breakdown the engine produced means the parts always
 * reconcile, because they are the parts that were charged.
 */

import { Cents, formatCents } from "./money";
import { PriceBreakdown, TaskPriceBreakdown } from "./decimal";

export type LineKind = "charge" | "discount" | "subtotal" | "total";

export interface MoneyLine {
  label: string;
  cents: Cents;
  /** Pre-rendered, always two decimal places, sign included for discounts. */
  formatted: string;
  kind: LineKind;
}

function line(label: string, cents: Cents, kind: LineKind): MoneyLine {
  const formatted =
    kind === "discount" && cents > 0 ? `-${formatCents(cents)}` : formatCents(cents);
  return { label, cents, formatted, kind };
}

/**
 * Display lines for a booking.
 *
 * Zero-valued discounts are omitted rather than rendered as `-$0.00`; a line
 * that promises a discount of nothing reads as a broken promise.
 *
 * Day-of-week surge is deliberately *not* a line. It is folded into the
 * appointment value, because the surge is how much the appointment costs on that
 * day rather than a fee added to it — see `CONTEXT.md`, which defines it as
 * applied invisibly. Same-Day is the opposite: the customer chose it by name and
 * knowingly, so it is itemized. Both sit above the subtotal, since both are
 * already inside `subTotalCents`, which is what keeps `linesReconcile` true.
 */
export function toBookingLines(breakdown: PriceBreakdown): MoneyLine[] {
  const lines: MoneyLine[] = [
    line(
      "Appointment value",
      breakdown.baseCents + breakdown.dayOfWeekSurgeCents,
      "charge"
    ),
  ];

  /**
   * A time-of-day premium is its own line, not folded into the appointment
   * value the way the day-of-week surge above is.
   *
   * The two look alike and are not. Surge is invisible by policy: the customer
   * is never offered a cheaper day, so naming it would itemise a choice they
   * do not have. A time-of-day premium is the opposite — the funnel prints a
   * price on every arrival slot and the customer picks one knowingly, which is
   * the same standing Same-Day has, and Same-Day is itemised for exactly that
   * reason. A premium folded silently into "Appointment value" would also make
   * the same booking show two different appointment values at two different
   * times of day with nothing on the receipt to explain either.
   */
  if (breakdown.timeOfDayPremiumCents > 0) {
    lines.push(line("Off-peak premium", breakdown.timeOfDayPremiumCents, "charge"));
  }

  if (breakdown.sameDaySurchargeCents > 0) {
    lines.push(line("Same-Day service", breakdown.sameDaySurchargeCents, "charge"));
  }
  /**
   * Both proximity lines are shown only when they carry an amount.
   *
   * A "Nearby discount −$0.00" line is a promise the customer can see we did not
   * keep, and a "+$0.00" premium reads as a charge that failed rather than one
   * that did not apply. The `> 0` guard here is the same rule every other
   * optional line follows; it is called out because the prototype shipped a
   * $0 Same-Day banner once already.
   */
  if (breakdown.travelPremiumCents > 0) {
    lines.push(line("Travel fee", breakdown.travelPremiumCents, "charge"));
  }
  if (breakdown.frequencyDiscountCents > 0) {
    lines.push(line("Frequency discount", breakdown.frequencyDiscountCents, "discount"));
  }
  if (breakdown.flexibleDiscountCents > 0) {
    lines.push(line("Flexible time discount", breakdown.flexibleDiscountCents, "discount"));
  }
  if (breakdown.proximityDiscountCents > 0) {
    lines.push(line("Nearby discount", breakdown.proximityDiscountCents, "discount"));
  }
  /**
   * Listed after the nearby discount and before the coupon, which is the order
   * the engine clamps them in (`getBookingPrice`). A reader adding the lines up
   * in the order they are printed then gets the same running total the engine
   * had, which matters on a booking whose discounts exceed it: the coupon is
   * the line that absorbs the shortfall, and it has to be last to look it.
   */
  if (breakdown.timeOfDayDiscountCents > 0) {
    lines.push(line("Off-peak discount", breakdown.timeOfDayDiscountCents, "discount"));
  }
  if (breakdown.promoDiscountCents > 0) {
    lines.push(line("Promo code", breakdown.promoDiscountCents, "discount"));
  }
  if (breakdown.addOnsCents > 0) {
    lines.push(line("Add-ons", breakdown.addOnsCents, "charge"));
  }

  lines.push(line("Subtotal", breakdown.subTotalCents, "subtotal"));
  lines.push(line("Sales tax (8.875%)", breakdown.taxCents, "charge"));
  lines.push(line("Total", breakdown.totalCents, "total"));
  return lines;
}

/** Display lines for a task booking. */
export function toTaskLines(breakdown: TaskPriceBreakdown): MoneyLine[] {
  const lines: MoneyLine[] = [line("Subtotal", breakdown.subTotalCents, "subtotal")];

  if (breakdown.promoDiscountCents > 0) {
    /**
     * The coupon, and above it what it came off.
     *
     * The promo line alone used to sit above a subtotal that was already net of
     * it, so the receipt read "Promo code −$25.00 / Subtotal $76.73" with
     * nothing saying the services came to $101.73. Two printed numbers that do
     * not relate to each other is the same defect this module's header opens
     * with, and `linesReconcile` now refuses it.
     *
     * Only when there is a coupon: with nothing taken off, the subtotal *is*
     * the services total, and printing it twice under two names explains
     * nothing.
     */
    lines.splice(
      0,
      0,
      line(
        "Services",
        breakdown.subTotalCents + breakdown.promoDiscountCents,
        "charge"
      ),
      line("Promo code", breakdown.promoDiscountCents, "discount")
    );
  }
  lines.push(line("Sales tax (8.875%)", breakdown.taxCents, "charge"));
  if (breakdown.tipCents > 0) {
    lines.push(line("Tip", breakdown.tipCents, "charge"));
  }
  lines.push(line("Total", breakdown.totalCents, "total"));
  if (breakdown.creditsAppliedCents > 0) {
    lines.push(line("Credits applied", breakdown.creditsAppliedCents, "discount"));
    lines.push(line("Amount due", breakdown.amountDueCents, "total"));
  }
  return lines;
}

/**
 * Assert that a set of lines reconciles: charges minus discounts equals the
 * final total. Call it in tests and before sending a receipt — a receipt whose
 * arithmetic is visibly wrong is the most common support ticket a pricing
 * change produces.
 */
export function linesReconcile(lines: MoneyLine[]): boolean {
  const subtotalIndex = lines.findIndex((l) => l.kind === "subtotal");
  const total = lines.filter((l) => l.kind === "total").pop();
  if (subtotalIndex === -1 || !total) return false;

  const money = (from: MoneyLine[]): MoneyLine[] =>
    from.filter((l) => l.kind === "charge" || l.kind === "discount");
  const net = (from: MoneyLine[]): Cents =>
    money(from).reduce((acc, l) => acc + (l.kind === "discount" ? -l.cents : l.cents), 0);

  const subTotal = lines[subtotalIndex].cents;
  const above = money(lines.slice(0, subtotalIndex));

  /**
   * Both halves are checked, and the top half is the half that matters.
   *
   * This used to anchor on the subtotal and add up only the lines *below* it,
   * on the reasoning that the lines above are inputs already reflected in its
   * value. True — and it makes the check blind to the only failure a reader
   * can actually see. Tax and the total are two lines that almost cannot be
   * wrong; the charges and discounts above the subtotal are eight that can,
   * and "these numbers do not add up to that one" is the complaint a receipt
   * generates.
   *
   * The hunt found exactly that: a booking carrying a time-of-day premium
   * printed an appointment value, a subtotal $12.21 larger, and nothing in
   * between to account for the difference — and this function called it
   * correct. A line set that is short a charge above the subtotal now fails
   * here, which is where a missing line is cheap to notice.
   *
   * The rule is "if you itemise, itemise completely", not "always itemise". A
   * set with nothing above the subtotal — the task receipt with no coupon —
   * makes no claim about how the subtotal was reached, and there is nothing
   * there for a reader to find wrong. One that itemises part of the way is the
   * broken case.
   */
  return (
    (above.length === 0 || net(above) === subTotal) &&
    subTotal + net(lines.slice(subtotalIndex + 1)) === total.cents
  );
}

/** A plain-text money summary, for Slack. */
export function toPlainText(lines: MoneyLine[]): string {
  return lines.map((l) => `${l.label}: ${l.formatted}`).join("\n");
}

/**
 * The money lines a receipt email prints, in cents.
 *
 * The email templates are hosted outside this repo and print Appointment,
 * Same-Day, Add-ons, Discount and Subtotal. They know nothing of area pricing,
 * so the nearby discount is folded into Discount and the travel fee into
 * Appointment — the way surge already is — and the printed lines add up to the
 * printed subtotal:
 *
 *   appointmentValue + sameDaySurcharge + addOns − discount = subTotal
 *
 * Both also come back on their own, for a template that names them.
 */
export function receiptAmounts(breakdown: PriceBreakdown): {
  appointmentValueCents: Cents;
  sameDaySurchargeCents: Cents;
  addOnsCents: Cents;
  discountCents: Cents;
  nearbyDiscountCents: Cents;
  travelFeeCents: Cents;
  subTotalCents: Cents;
  taxCents: Cents;
} {
  return {
    /**
     * The time-of-day premium is folded in here, unlike in `toBookingLines`
     * above, because these templates live outside this repo and print a fixed
     * set of lines — there is no line to add. Folding it into Appointment is
     * the same treatment surge and the travel fee already get, and it is what
     * keeps the printed lines summing to the printed subtotal.
     */
    appointmentValueCents:
      breakdown.baseCents +
      breakdown.dayOfWeekSurgeCents +
      breakdown.travelPremiumCents +
      breakdown.timeOfDayPremiumCents,
    sameDaySurchargeCents: breakdown.sameDaySurchargeCents,
    addOnsCents: breakdown.addOnsCents,
    discountCents:
      breakdown.frequencyDiscountCents +
      breakdown.flexibleDiscountCents +
      breakdown.promoDiscountCents +
      breakdown.proximityDiscountCents +
      breakdown.timeOfDayDiscountCents,
    nearbyDiscountCents: breakdown.proximityDiscountCents,
    travelFeeCents: breakdown.travelPremiumCents,
    subTotalCents: breakdown.subTotalCents,
    taxCents: breakdown.taxCents,
  };
}
